import { Tooltip } from "@/base";
import { useFormControllerContext } from "@/contexts";
import {
    Button,
    cn,
    Textarea as NextUiTextarea,
    type TextAreaProps as NextUiTextareaProps,
} from "@heroui/react";
import { IconBulb, IconInfoCircle } from "@tabler/icons-react";
import {
    type ChangeEvent,
    type ReactNode,
    useCallback,
    useEffect,
    useRef,
} from "react";
import {
    type FieldValues,
    type UseControllerProps,
    useController as useFieldController,
} from "react-hook-form";
import { type AIContext, MagicWand, type MagicWandProps } from "../magic-wand";

type TextareaProps<T extends FieldValues, AIContextKeys extends string> = {
    label?: string;
    fieldName: UseControllerProps<T>["name"];
    placeholder?: string;
    description?: ReactNode;
    hint?: ReactNode;
    isAutofocusing?: boolean;
    isDisabled?: boolean;
    isRequired?: boolean;
    minRows?: number;
    maxRows?: number;
    labelPlacement?: NextUiTextareaProps["labelPlacement"];
    classNames?: NextUiTextareaProps["classNames"];
    className?: string;
    maxLength?: number;
    isViewOnly?: boolean;
    endContent?: ReactNode;
    ai?: Pick<
        MagicWandProps<AIContext<AIContextKeys>, string>,
        | "context"
        | "onCreate"
        | "onUpdate"
        | "showPopover"
        | "additionalInformationEnabled"
        | "additionalInformationRequired"
        | "additionalInformationLabel"
        | "additionalInformationHint"
        | "rotateResultsAfter"
    > & { uniqueId: string | number };
    onBlur?: NextUiTextareaProps["onBlur"];
    onFocus?: NextUiTextareaProps["onFocus"];
    hideLabel?: boolean;
    onChange?: (value: string) => void;
    autofocusAtContendEnd?: boolean;
    disableLineBreak?: boolean;
};

const Textarea = <
    T extends FieldValues,
    AIContextKeys extends string = string,
>({
    description,
    hint,
    fieldName,
    isAutofocusing,
    isDisabled,
    label,
    isRequired,
    minRows,
    maxRows,
    placeholder,
    labelPlacement,
    classNames,
    className,
    ai,
    onBlur,
    onFocus,
    hideLabel,
    onChange,
    maxLength,
    isViewOnly,
    endContent,
    autofocusAtContendEnd,
    disableLineBreak,
}: TextareaProps<T, AIContextKeys>) => {
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const formController = useFormControllerContext();

    const fieldController = useFieldController<T>({
        control: formController.control,
        name: fieldName,
    });

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            let value = e.target.value;

            if (disableLineBreak) {
                value = value.replace(/[\r\n]+/g, " ");
            }

            fieldController.field.onChange(value);
            onChange?.(value);
        },
        [disableLineBreak, fieldController.field.onChange, onChange],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (disableLineBreak && e.key === "Enter") {
                e.preventDefault();
            }
        },
        [disableLineBreak],
    );

    useEffect(() => {
        if (inputRef.current && autofocusAtContendEnd) {
            setTimeout(() => {
                const el = inputRef.current;
                el.focus();
                const length = el.value.length;
                el.setSelectionRange(length, length);
            }, 50);
        }
    }, [autofocusAtContendEnd]);

    return (
        <NextUiTextarea
            ref={inputRef}
            autoFocus={isAutofocusing}
            isDisabled={isDisabled}
            placeholder={placeholder}
            labelPlacement={labelPlacement}
            className={cn(className, isViewOnly && "opacity-70")}
            classNames={{
                ...classNames,
                inputWrapper: cn(
                    isViewOnly && "pointer-events-auto cursor-auto!",
                    classNames?.inputWrapper,
                ),
                innerWrapper: cn(
                    (hideLabel || !label) && "items-center",
                    classNames?.innerWrapper,
                ),
            }}
            fullWidth={true}
            isInvalid={!!fieldController.fieldState.error}
            errorMessage={fieldController.fieldState.error?.message}
            label={hideLabel ? undefined : label}
            aria-label={label}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            value={fieldController.field.value || ""}
            isRequired={isRequired}
            minRows={minRows}
            maxRows={maxRows}
            onBlur={onBlur}
            onFocus={onFocus}
            maxLength={maxLength}
            endContent={
                <>
                    {endContent}
                    <div className="-mt-5 flex shrink-0 items-center gap-1">
                        {description && (
                            <Tooltip color="primary" content={description}>
                                <Button
                                    variant="light"
                                    isIconOnly={true}
                                    size="sm"
                                >
                                    <IconInfoCircle stroke={1.5} size={18} />
                                </Button>
                            </Tooltip>
                        )}
                        {hint && (
                            <Tooltip color="warning" content={hint}>
                                <Button
                                    variant="light"
                                    isIconOnly={true}
                                    size="sm"
                                >
                                    <IconBulb stroke={1.5} size={18} />
                                </Button>
                            </Tooltip>
                        )}
                        {ai && (
                            <MagicWand
                                {...ai}
                                id={`${ai.uniqueId}_${fieldName}`}
                                inputValue={fieldController.field.value}
                                onResult={fieldController.field.onChange}
                            />
                        )}
                    </div>
                </>
            }
        />
    );
};

export { Textarea };
